import { Suspense, lazy } from 'react';
import { Redirect } from 'react-router-dom';
import LoadingPage from '../components/LoadingPage/LoadingPage';
import { useFeatureFlag } from '../hooks/useFeatureFlag';
import { useApplicationContext } from '../modules/context/ApplicationContext/ApplicationContext';
import { isAdminTeceo } from '../modules/context/ApplicationContext/applicationContext.utils';
import HomeSwitch from '../modules/home/components/HomeSwitch';
import { useFeatureVisibility } from '../modules/permissions/contexts/FeatureVisibilityContext';
import { F } from '../modules/permissions/types/features.keys';
import { TargetPaths } from '../modules/target/target.paths';
import { useAbility } from '../modules/users/AbilityContext';
import { TargetType } from '../services/modules/target/target.types';
import { useAppEnvironmentStore } from '../stores/AppEnvironment/appEnvironment.store';
import { selectIsInStoreEnvironment } from '../stores/AppEnvironment/appEnvironment.store.selectors';
import { useRootStyles } from './routes-page.styles';
import PATHS from './routes.const';
import { TeceoRoute } from './routes.types';

const AdminPage = lazy(() => import('../modules/admin/AdminPage'));
const CatalogListPage = lazy(
  () => import('../modules/catalogs/pages/CatalogListPage/CatalogListPage'),
);

const CatalogScreen = lazy(
  () => import('../modules/catalogs/products/CatalogScreen/CatalogProductsRoute'),
);
const CustomerListPage = lazy(
  () => import('../modules/customer/pages/CustomerListPage/CustomerListPage'),
);
const CustomerObjectPage = lazy(
  () => import('../modules/customer/pages/CustomerObjectPage/CustomerObjectPage'),
);
const EventPage = lazy(() => import('../modules/events/EventPage'));
const SalesOpportunityAdvancedTabs = lazy(
  () => import('../modules/sales-opportunity/SalesOpportunityAdvancedTabs'),
);
const SalesOpportunityTabs = lazy(
  () => import('../modules/sales-opportunity/SalesOpportunityTabs'),
);
const MyNetworkPage = lazy(() => import('../modules/my-network/MyNetworkPage'));
const OrderListPage = lazy(() => import('../modules/orders/pages/OrderListPage/OrderListPage'));
const StoreOrderListPage = lazy(
  () => import('../modules/store-orders/pages/StoreOrderListPage/StoreOrderListPage'),
);
const StoreOrderObjectPage = lazy(
  () => import('../modules/store-orders/pages/StoreOrderObjectPage/StoreOrderObjectPage'),
);
const OrderEditPage = lazy(() => import('../modules/orders/pages/OrderObjectPage/OrderObjectPage'));
const InvoiceListPage = lazy(
  () => import('../modules/invoices/pages/InvoiceListPage/InvoiceListPage'),
);
const ProductPage = lazy(() => import('../modules/products/ProductPage'));
const RachelPage = lazy(() => import('../modules/rachel/RachelPage'));
const SettingsPage = lazy(() => import('../modules/settings/SettingsPage'));
const ShowroomOrStorefront = lazy(
  () => import('../modules/showroom-storefront/ShowroomOrStorefront'),
);
const ReportsRouter = lazy(() => import('../modules/reports/ReportsRouter'));
const AnalyticsRouter = lazy(() => import('../modules/reports/AnalyticsRouter'));
const LightdashReportsPage = lazy(
  () =>
    import('../modules/reports/lightdash-reports/pages/LightdashReportsPage/LightdashReportsPage'),
);
const CaseList = lazy(() => import('../modules/cases/list/CaseListPage'));
const TargetListPage = lazy(() => import('../modules/target/list/TargetListPage/TargetListPage'));
const TargetObjectPage = lazy(
  () => import('../modules/target/object/TargetObjectPage/TargetObjectPage'),
);
const CaseFormPage = lazy(() => import('../modules/cases/form/CaseFormPage'));
const PayableAccountsReceivableListPage = lazy(
  () => import('../modules/accountsReceivable/pages/PayableInstallmentsListPage'),
);
const ReceivableAccountsReceivableListPage = lazy(
  () => import('../modules/accountsReceivable/pages/ReceivableInstallmentsListPage'),
);
const useHomeRoutes = () => {
  const rootStyles = useRootStyles();

  const ability = useAbility();
  const { isHidden, isVisible } = useFeatureVisibility();
  const { isStore, isCustomRole } = useApplicationContext();
  const isStoreEnv = useAppEnvironmentStore(selectIsInStoreEnvironment);
  const { isEnableCases, isEnableInvoices, isAccountsReceivableEnabled, isEnableRachelChat } =
    useFeatureFlag();

  const homeRoutes: TeceoRoute[] = [
    {
      name: 'rachel',
      validateAbility: false,
      disabled: !isEnableRachelChat,
      path: PATHS.main.rachel.path,
      exact: true,
      render: () => (
        <Suspense fallback={<LoadingPage backgroundless />}>
          <RachelPage />
        </Suspense>
      ),
    },
    {
      name: 'connections',
      path: PATHS.main.myNetwork.path,
      render: (props) => (
        <Suspense fallback={<LoadingPage backgroundless />}>
          <MyNetworkPage {...props} rootStyles={rootStyles} />
        </Suspense>
      ),
    },
    {
      name: 'brands',
      path: PATHS.main.adminTeceo.path,
      disabled: !isAdminTeceo(),
      render: (props) => (
        <Suspense fallback={<LoadingPage backgroundless />}>
          <AdminPage {...props} rootStyles={rootStyles} />
        </Suspense>
      ),
    },
    {
      name: 'lightdash-reports',
      validateAbility: false,
      path: PATHS.reports.report.lightdashReports.dashboard.path,
      render: () => (
        <Suspense fallback={<LoadingPage backgroundless />}>
          <LightdashReportsPage />
        </Suspense>
      ),
    },
    {
      name: 'reports',
      path: PATHS.main.reports.path,
      render: (props) => (
        <Suspense fallback={<LoadingPage backgroundless />}>
          <ReportsRouter {...props} rootStyles={rootStyles} />
        </Suspense>
      ),
    },
    {
      name: 'lightdash-reports',
      validateAbility: false,
      path: PATHS.main.analytics.path,
      render: () => (
        <Suspense fallback={<LoadingPage backgroundless />}>
          <AnalyticsRouter />
        </Suspense>
      ),
    },
    {
      name: 'settings',
      path: PATHS.main.settings.path,
      render: (props) => (
        <Suspense fallback={<LoadingPage backgroundless />}>
          <SettingsPage {...props} rootStyles={rootStyles} />
        </Suspense>
      ),
    },
    {
      name: 'customers',
      path: PATHS.main.customers.path,
      exact: true,
      disabled: isStore,
      render: (props) => (
        <Suspense fallback={<LoadingPage backgroundless />}>
          <CustomerListPage {...props} rootStyles={rootStyles} />
        </Suspense>
      ),
    },
    {
      name: 'customers',
      path: PATHS.main.customers.form.path,
      disabled: isStore,
      render: (props) => (
        <Suspense fallback={<LoadingPage backgroundless />}>
          <CustomerObjectPage {...props} rootStyles={rootStyles} />
        </Suspense>
      ),
    },
    {
      name: 'sales-opportunities',
      path: PATHS.main.salesOpportunities.path,
      exact: true,
      render: (props) => (
        <Suspense fallback={<LoadingPage backgroundless />}>
          <SalesOpportunityAdvancedTabs {...props} rootStyles={rootStyles} />
        </Suspense>
      ),
    },
    {
      name: 'sales-opportunities',
      path: PATHS.main.salesOpportunities.form.path,
      render: (props) => (
        <Suspense fallback={<LoadingPage backgroundless />}>
          <SalesOpportunityTabs {...props} rootStyles={rootStyles} />
        </Suspense>
      ),
    },
    {
      name: 'targets',
      path: PATHS.main.targets.list.path,
      exact: true,
      disabled: isHidden(F.menu.targets),
      render: () => (
        <Suspense fallback={<LoadingPage backgroundless />}>
          <TargetListPage
            type={isStoreEnv ? TargetType.STORE_TO_ITSELF : TargetType.BRAND_TO_CUSTOMER}
          />
        </Suspense>
      ),
    },
    {
      name: 'targets',
      path: PATHS.main.targets.form.path,
      exact: true,
      disabled: isHidden(F.menu.targets),
      render: () => (
        <Suspense fallback={<LoadingPage backgroundless />}>
          <TargetObjectPage />
        </Suspense>
      ),
    },
    {
      name: 'targets',
      path: PATHS.main.legacyTargets.path,
      exact: true,
      render: ({ location }) => <Redirect to={`${TargetPaths.list()}${location.search}`} />,
    },
    {
      name: 'targets',
      path: PATHS.main.legacyTargets.form.path,
      exact: true,
      render: ({ location, match }) => (
        <Redirect to={`${TargetPaths.detail(String(match.params.targetId))}${location.search}`} />
      ),
    },
    {
      name: 'catalogs',
      path: PATHS.main.products.path,
      exact: true,
      render: (props) => (
        <Suspense fallback={<LoadingPage backgroundless />}>
          <ProductPage {...props} rootStyles={rootStyles} />
        </Suspense>
      ),
    },
    {
      name: 'catalogs',
      path: PATHS.main.catalogs.path,
      exact: true,
      render: (props) => (
        <Suspense fallback={<LoadingPage backgroundless />}>
          <CatalogListPage {...props} rootStyles={rootStyles} />
        </Suspense>
      ),
    },
    {
      name: 'catalogs',
      path: PATHS.main.catalogs.products.path,
      exact: true,
      render: (props) => (
        <Suspense fallback={<LoadingPage backgroundless />}>
          <CatalogScreen {...props} rootStyles={rootStyles} canPersist />
        </Suspense>
      ),
    },
    {
      name: 'orders',
      path: PATHS.main.orders.path,
      exact: true,
      render: (props) => (
        <Suspense fallback={<LoadingPage backgroundless />}>
          <OrderListPage {...props} rootStyles={rootStyles} />
        </Suspense>
      ),
    },
    {
      name: 'store-orders',
      path: PATHS.main.storeOrders.path,
      exact: true,
      disabled: isHidden(F.menu.storeOrders),
      render: (props) => (
        <Suspense fallback={<LoadingPage backgroundless />}>
          <StoreOrderListPage {...props} rootStyles={rootStyles} />
        </Suspense>
      ),
    },
    {
      name: 'store-orders',
      path: `${PATHS.main.storeOrders.path}/:id`,
      exact: true,
      disabled: isHidden(F.menu.storeOrders),
      render: (props) => (
        <Suspense fallback={<LoadingPage backgroundless />}>
          <StoreOrderObjectPage {...props} rootStyles={rootStyles} />
        </Suspense>
      ),
    },
    {
      name: 'invoices',
      path: PATHS.main.invoices.path,
      exact: true,
      disabled: !isEnableInvoices,
      render: (props) => (
        <Suspense fallback={<LoadingPage backgroundless />}>
          <InvoiceListPage {...props} rootStyles={rootStyles} />
        </Suspense>
      ),
    },
    {
      name: 'orders',
      path: PATHS.main.orders.editForm.path,
      render: () => (
        <Suspense fallback={<LoadingPage backgroundless />}>
          <OrderEditPage />
        </Suspense>
      ),
    },
    {
      name: 'orders',
      path: PATHS.main.orders.suggestion.path,
      render: () => (
        <Suspense fallback={<LoadingPage backgroundless />}>
          <OrderEditPage />
        </Suspense>
      ),
    },
    {
      name: 'orders',
      path: PATHS.main.orders.finished.path,
      render: () => (
        <Suspense fallback={<LoadingPage backgroundless />}>
          <OrderEditPage />
        </Suspense>
      ),
    },
    {
      name: 'events',
      path: PATHS.main.events.path,
      render: () => (
        <Suspense fallback={<LoadingPage backgroundless />}>
          <EventPage />
        </Suspense>
      ),
    },
    {
      name: 'cases',
      path: PATHS.main.cases.path,
      exact: true,
      disabled: !isEnableCases,
      render: (props) => (
        <Suspense fallback={<LoadingPage backgroundless />}>
          <CaseList {...props} rootStyles={rootStyles} />
        </Suspense>
      ),
    },
    {
      name: 'cases',
      path: PATHS.main.casesByType.path,
      exact: true,
      disabled: !isEnableCases,
      render: (props) => (
        <Suspense fallback={<LoadingPage backgroundless />}>
          <CaseList {...props} rootStyles={rootStyles} />
        </Suspense>
      ),
    },
    {
      name: 'cases',
      path: PATHS.main.cases.form.path,
      disabled: !isEnableCases,
      render: (props) => (
        <Suspense fallback={<LoadingPage backgroundless />}>
          <CaseFormPage {...props} rootStyles={rootStyles} />
        </Suspense>
      ),
    },
    {
      name: 'accounts-receivable',
      path: PATHS.main.accountsReceivable.receivable.path,
      disabled:
        ability.cannot('menu', 'accounts-receivable') || !isAccountsReceivableEnabled || isStore,
      render: () => (
        <Suspense fallback={<LoadingPage backgroundless />}>
          <ReceivableAccountsReceivableListPage />
        </Suspense>
      ),
    },
    {
      name: 'accounts-receivable',
      path: PATHS.main.accountsReceivable.payable.path,
      disabled: ability.cannot('menu', 'accounts-receivable') || !isStore,
      render: () => (
        <Suspense fallback={<LoadingPage backgroundless />}>
          <PayableAccountsReceivableListPage />
        </Suspense>
      ),
    },
    {
      name: isStore ? 'storefront' : 'showroom',
      path: PATHS.main.showroomStorefront.path,
      render: () => (
        <Suspense fallback={<LoadingPage backgroundless />}>
          <ShowroomOrStorefront />
        </Suspense>
      ),
      validateAbility: isCustomRole ? ability.cannot('read', 'storefront') : true,
    },
    {
      name: 'abilities',
      path: PATHS.main.home.path,
      render: () =>
        isVisible(F.menu.home) ? <HomeSwitch /> : <Redirect to={PATHS.main.rachel.path} />,
    },
  ];

  return homeRoutes;
};

export default useHomeRoutes;
